import React, { useState, useCallback } from "react";
import _, { at, set } from "lodash";
import classNames from "classnames";
import Dropzone, { DropzoneState, DropzoneRef } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { QualityAlert } from "../../../models/quality-alert";
import ReactModal from "react-modal";
import { Attachment } from "../../../models/attachment";
import { 
  deleteAttachment, 
  saveAttachment as saveAttachmentThunk,
  selectAttachmentLoading,
  selectAttachmentError 
} from "../../../reducers/attachmentSlice";
import { useAppDispatch, useAppSelector } from "../../../reducers/store";

// Define the types for props
interface AttachmentsProps {
  qualityAlert: any;
  className?: string;
  updateQualityAlert: (qualityAlert: QualityAlert) => void;
}

interface TableContainerProps {
  data: any[];
  onRowClick: (index: number, row: any) => void;
  selectedRow: number | null;
  onDeleteClick: (id: number, index: number) => void;
  className?: string;
}

// Table Container component with regular HTML table
const TableContainer: React.FC<TableContainerProps> = ({
  data,
  onRowClick,
  selectedRow,
  onDeleteClick,
  className,
}) => {
  const summaryDataGetter = (row: any) => {
    let summary = row.attachment?.summary;
    return summary;
  };

  const handleRowClick = (index: number) => {
    onRowClick(index, data[index]);
  };

  return (
    <div className={className}>
      <table className="min-w-full border-collapse border border-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th className="border border-gray-200 p-2 text-left">Filename</th>
            <th className="border border-gray-200 p-2 text-left">Summary</th>
            <th className="border border-gray-200 p-2 text-left"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr
              key={row.attachment.id}
              className={classNames("cursor-pointer hover:bg-gray-100", {
                "bg-blue-50": selectedRow === index,
              })}
              onClick={() => handleRowClick(index)}
            >
              <td className="border border-gray-200 p-2">
                {row.attachment.filename}
              </td>
              <td className="border border-gray-200 p-2">
                {summaryDataGetter(row)}
              </td>
              <td
                className="cursor-pointer border border-gray-200 p-2 text-red-500"
                onClick={() => onDeleteClick(row.attachment.id, index)}
              >
                Delete
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Attachments component
const Attachments: React.FC<AttachmentsProps> = ({
  qualityAlert,
  className,
  updateQualityAlert,
}) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAttachmentLoading);
  const error = useAppSelector(selectAttachmentError);

  const onDrop = useCallback((acceptedFiles: any) => {
    // Do something with the files
    acceptedFiles.forEach((file: any) => {
      attachment.filename = file.name;
      setAttachment({ ...attachment });
      setFile(file);
      setModalIsOpen(true);
    });
    setModalIsOpen(true);
  }, []);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [isModalOpen, setModalIsOpen] = useState<boolean>(false);
  let [uploads, setUploads] = useState<any>({});
  const [attachment, setAttachment] = useState<Attachment>({} as Attachment);
  const [attachmentList, setAttachmentList] = useState<any[]>([]);
  const [file, setFile] = useState<any>(null);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    onDrop,
  });

  const onRowClick = (index: number, row: any) => {
    setSelectedRow((prev) => (prev === index ? null : index));
  };

  const onRowDoubleClick = (row: any) => {
    window.open(row.attachment.downloadURL, "_blank");
  };

  // const onAddClick = (e: any) => {
  //   console.log("Add attachment clicked", e);
  // };

  // const onSave = (id: number, data: any, fileSize: number) => {
  //   const updatedUploads = {
  //     ...uploads,
  //     [id]: { ...data, progress: 0.0, size: fileSize },
  //   };
  //   setUploads(updatedUploads);

  //   //actionsQualityAlerts.linkAttachment(qualityAlertId, data, true);
  // };

  // const onProgress = (id: number, progress: number, size: number) => {
  //   const updatedUploads = {
  //     ...uploads,
  //     [id]: { ...uploads[id], progress, size },
  //   };
  //   setUploads(updatedUploads);
  // };

  const onComplete = (id: string | number, attachment: any) => {
    uploads[id] = attachment;
    const updatedUploads = {
      ...uploads,
    };
    setUploads(updatedUploads);
    attachmentList.push({ attachment });
    setAttachmentList(attachmentList);
  };

  const onDeleteFile = (id: number, index: number) => {
    attachmentList.splice(index, 1);
    setAttachmentList(attachmentList);
    dispatch(deleteAttachment(id));

    qualityAlert.attachments =
      qualityAlert?.attachments.filter(
        (attachment: any) => attachment.attachment !== id,
      ) || [];
    updateQualityAlert({ ...qualityAlert });
  };

  const getAttachments = () => {
    const data = qualityAlert.attachments || [];
    return data.map((attachmentLink: any) => attachmentLink.attachment);
  };

  const textAreaChanged = (value: string) => {
    attachment.summary = value;
    setAttachment({ ...attachment });
  };

  const saveAttachment = async () => {
    if (isSaveDisabled) {
      return;
    }
    setIsSaveDisabled(true);
    
    const result = await dispatch(saveAttachmentThunk({
      attachment,
      uploadId: null,
      onComplete,
      file,
    }));

    if (result.payload) {
      const id = result.payload.id;
      const attachmentArray = qualityAlert?.attachments || [];
      attachmentArray.push({ attachment: id });
      qualityAlert.attachments = attachmentArray;
      updateQualityAlert({ ...qualityAlert });
      setModalIsOpen(false);
    }
    
    setIsSaveDisabled(false);
  };

  const linkedData = getAttachments();
  const unlinkedData: any[] = [];

  // linkedData.forEach((link: any) => {
  //   const theUploadIndex = _.findIndex(linkedData, { id: link.id });
  //   console.log("The upload index:", theUploadIndex);
  //   if (theUploadIndex === -1) {
  //     unlinkedData.push(link);
  //   }
  // });

  const data = _.union(linkedData, unlinkedData);
  let uploading = null;

  // uploads = data.filter(
  //   (upload) => upload.progress != null && upload.progress !== 100,
  // );
  // const totalUploaded = uploads.reduce(
  //   (acc: number, upload: { size: number; progress: number }) =>
  //     acc + upload.size * upload.progress,
  //   0,
  // );

  // if (totalSize !== 0) {
  //   uploading = `${Math.floor((totalUploaded / totalSize) * 100)}% complete.`;
  // }

  let downloadButton = null;
  if (selectedRow != null) {
    const row = attachmentList[selectedRow];
    if (row) {
      downloadButton = (
        <span
          className="cursor-pointer text-blue-500"
          onClick={() => onRowDoubleClick(row)}
        >
          Download File
        </span>
      );
    }
  }

  return (
    <div
      {...getRootProps()}
      className={classNames(className, "flex flex-col space-y-4")}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center justify-center space-y-2">
        <div
          className="cursor-pointer rounded bg-blue-500 px-4 py-2 text-white"
          onClick={open}
        >
          + Attachment
        </div>
        <div>{uploading}</div>
      </div>
      <div className="text-black">
        <TableContainer
          data={attachmentList}
          onRowClick={onRowClick}
          selectedRow={selectedRow}
          onDeleteClick={onDeleteFile}
        />
      </div>
      <div>{downloadButton}</div>
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className="relative w-96 rounded bg-white p-6 shadow-md"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <h2 className="mb-4 align-middle text-lg font-bold text-black">
          Add Attachment
        </h2>
        <textarea
          className="h-40 w-full border border-gray-300 bg-white p-2 text-black"
          placeholder="Add a description..."
          onChange={(evt) => textAreaChanged(evt.target.value)}
        />
        <div className="flex justify-end">
          <div
            className="mt-2 cursor-pointer rounded bg-green-600 p-1.5 text-center text-sm text-white hover:bg-gray-400"
            onClick={saveAttachment}
          >
            {"Save"}
          </div>
        </div>
      </ReactModal>
    </div>
  );
};

export default Attachments;
